'use client';

import React from 'react';
import PhoneInput from 'react-phone-input-2';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { cn } from '@/lib/utils';
import 'react-phone-input-2/lib/style.css';

interface CountryData {
  dialCode: string;
  countryCode: string;
  name: string;
  format?: string;
}

interface FormPhoneInputProps<T extends FieldValues> {
  label?: string;
  name: Path<T>;
  control: Control<T>;
  error?: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  onCountryChange?: (
    countryCode: string,
    countryData: CountryData,
    formattedValue: string
  ) => void;
}

export function FormPhoneInput<T extends FieldValues>({
  label,
  name,
  control,
  error,
  className,
  placeholder = 'Enter phone number',
  disabled = false,
  onCountryChange,
}: FormPhoneInputProps<T>) {
  return (
    <div className={cn('w-full', className)}>
      {label && (
        <label className="mb-1.5 block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}

      <div className="relative">
        <Controller
          name={name}
          control={control}
          render={({ field: { onChange, value } }) => (
            <PhoneInput
              country={'in'}
              value={value}
              onChange={(phone, countryData, event, formattedValue) => {
                onChange(phone);
                if (
                  onCountryChange &&
                  countryData &&
                  'dialCode' in countryData
                ) {
                  onCountryChange(
                    `+${countryData.dialCode}`,
                    countryData as CountryData,
                    formattedValue
                  );
                }
              }}
              placeholder={placeholder}
              disabled={disabled}
              inputProps={{
                autoComplete: 'tel',
              }}
              inputClass={cn(
                'form-input !w-full !rounded-lg !border-gray-300 !pl-12 !shadow-sm !transition-all !duration-200',
                '!text-sm focus:!border-primary-500 focus:!ring-primary-500',
                '!bg-white/100 hover:!bg-white focus:!bg-white',
                error &&
                  '!border-red-300 focus:!border-red-500 focus:!ring-red-500',
                disabled && '!cursor-not-allowed !bg-gray-100 !opacity-75'
              )}
              containerClass="!w-full"
              buttonClass={cn(
                '!border-0 !bg-transparent !rounded-l-lg',
                disabled && '!cursor-not-allowed !opacity-75'
              )}
              dropdownClass="!bg-white !border !border-gray-200 !rounded-lg !shadow-lg !z-[99999]"
              enableSearch={true}
              disableSearchIcon={true}
            />
          )}
        />
      </div>

      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
